/**
 * SequenceSettingsRail — 340 px sticky right rail for sequence delivery settings (§11-F).
 *
 * Fields:
 *   - Preview recipient dropdown
 *   - Delivery window (start / end / timezone)
 *   - Business days only
 *   - First emails per day
 *   - Emails per hour
 *   - Send from (read-only)
 *   - Use email signature
 */

import { Info } from 'lucide-react';

export interface SequenceSettings {
  preview_recipient: string;
  window_start: string;      // "09:00"
  window_end: string;        // "17:00"
  timezone: string;          // e.g. "America/New_York"
  business_days_only: boolean;
  first_emails_per_day: number;
  emails_per_hour: number;
  use_signature: boolean;
}

export const DEFAULT_SEQUENCE_SETTINGS: SequenceSettings = {
  preview_recipient: '',
  window_start: '09:00',
  window_end: '17:00',
  timezone: 'America/New_York',
  business_days_only: true,
  first_emails_per_day: 50,
  emails_per_hour: 6,
  use_signature: true,
};

const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

interface SequenceSettingsRailProps {
  settings: SequenceSettings;
  onChange: (patch: Partial<SequenceSettings>) => void;
  /** Array of "{name} <email>" strings for the preview recipient dropdown. */
  recipientOptions?: string[];
  /** Shown in the read-only "Send from" field. */
  senderEmail?: string | null;
}

export function SequenceSettingsRail({
  settings,
  onChange,
  recipientOptions = [],
  senderEmail,
}: SequenceSettingsRailProps) {
  return (
    <aside
      className="shrink-0 w-[340px] rounded-[12px] border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-primary)] overflow-hidden"
    >
      <div className="border-b border-[var(--vscode-border-primary)] px-4 py-3">
        <h3 className="text-[13px] font-semibold vscode-text-primary">Delivery settings</h3>
        <p className="mt-0.5 text-[11px] vscode-text-tertiary">
          Applied to every recipient in this sequence.
        </p>
      </div>

      <div className="divide-y divide-[var(--vscode-border-primary)]">
        {/* Preview recipient */}
        <Section label="Preview recipient">
          <select
            value={settings.preview_recipient}
            onChange={(e) => onChange({ preview_recipient: e.target.value })}
            className="input text-[12px] h-8 w-full"
          >
            <option value="">None (show variables as tokens)</option>
            {recipientOptions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Section>

        {/* Delivery window */}
        <Section label="Delivery time window">
          <div className="flex items-center gap-1.5">
            <input
              type="time"
              value={settings.window_start}
              onChange={(e) => onChange({ window_start: e.target.value })}
              className="input text-[12px] h-8 flex-1"
            />
            <span className="vscode-text-tertiary text-[12px]">to</span>
            <input
              type="time"
              value={settings.window_end}
              onChange={(e) => onChange({ window_end: e.target.value })}
              className="input text-[12px] h-8 flex-1"
            />
          </div>
          <select
            value={settings.timezone}
            onChange={(e) => onChange({ timezone: e.target.value })}
            className="input text-[12px] h-8 w-full mt-1.5"
          >
            {COMMON_TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </Section>

        {/* Business days */}
        <ToggleSection
          label="Business days only"
          hint="Skip Saturdays and Sundays."
          checked={settings.business_days_only}
          onChange={(v) => onChange({ business_days_only: v })}
        />

        {/* Throughput */}
        <Section label="Throughput">
          <label className="flex items-center justify-between gap-2">
            <span className="text-[12px] vscode-text-secondary">First emails / day</span>
            <input
              type="number"
              min={1}
              max={500}
              value={settings.first_emails_per_day}
              onChange={(e) => onChange({ first_emails_per_day: Math.max(1, Number(e.target.value) || 1) })}
              className="input text-[12px] h-8 w-20 text-right"
            />
          </label>
          <label className="flex items-center justify-between gap-2 mt-1.5">
            <span className="text-[12px] vscode-text-secondary">Emails / hour</span>
            <input
              type="number"
              min={1}
              max={60}
              value={settings.emails_per_hour}
              onChange={(e) => onChange({ emails_per_hour: Math.max(1, Number(e.target.value) || 1) })}
              className="input text-[12px] h-8 w-20 text-right"
            />
          </label>
        </Section>

        {/* Send from */}
        <Section label="Send from">
          <input
            type="email"
            value={senderEmail ?? ''}
            readOnly
            placeholder="(connect a sending mailbox in Settings)"
            className="input text-[12px] h-8 w-full bg-[var(--vscode-bg-primary)] vscode-text-tertiary cursor-not-allowed"
          />
          <p className="mt-1 flex items-start gap-1 text-[11px] vscode-text-tertiary">
            <Info className="w-3 h-3 shrink-0 mt-0.5" />
            <span>Configure in Settings → Email.</span>
          </p>
        </Section>

        {/* Signature */}
        <ToggleSection
          label="Use email signature"
          hint="Appends your configured signature to each block."
          checked={settings.use_signature}
          onChange={(v) => onChange({ use_signature: v })}
        />
      </div>
    </aside>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="px-4 py-3">
      <label className="block text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary mb-1.5">
        {label}
      </label>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function ToggleSection({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="px-4 py-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[12px] font-medium vscode-text-primary">{label}</p>
        {hint && <p className="mt-0.5 text-[11px] vscode-text-tertiary">{hint}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-emerald-600' : 'bg-[var(--vscode-bg-tertiary)]'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white  transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}
